// C:\Projects\texe\texe-web\src\pages\Register.tsx
import { useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiPost, setToken } from "../lib/api";
import { useAuth } from "../context/AuthProvider";

type RegisterResponse = {
  ok: true;
  access?: string;
  token?: string;
  accessToken?: string;
  user?: { id: string; username: string; email: string };
};

export default function Register() {
  const nav = useNavigate();
  const { refreshMe } = useAuth();

  const [form, setForm] = useState({ 
    username: "", 
    email: "", 
    password: "", 
    password2: "",
    firstName: "",
    lastName: ""
  });
  const [demographics, setDemographics] = useState({
    birthDay: "",
    birthMonth: "",
    birthYear: "",
    gender: ""
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    // Client-side validation
    if (!form.username || !form.email || !form.password || !form.password2) {
      setError('Tüm alanları doldur');
      return;
    }

    if (form.username.length < 3) {
      setError('Kullanıcı adı en az 3 karakter olmalı');
      return;
    }

    // Username validation
    const usernameRegex = /^[a-zA-Z0-9_-]+$/;
    if (!usernameRegex.test(form.username)) {
      setError('Kullanıcı adı sadece harf, rakam, tire ve alt çizgi içerebilir');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      setError('Geçerli bir e-posta adresi gir');
      return;
    }

    if (form.password.length < 6) {
      setError('Şifre en az 6 karakter olmalı');
      return;
    }

    if (form.password !== form.password2) {
      setError('Şifreler uyuşmuyor');
      return;
    }

    if (!acceptedTerms) {
      setError('Kullanıcı sözleşmesini kabul etmelisin');
      return;
    }

    setLoading(true);

    try {
      // Demographics verilerini hazırla (YYYY-MM-DD formatında)
      let dateOfBirth = null;
      if (demographics.birthDay && demographics.birthMonth && demographics.birthYear) {
        const day = parseInt(demographics.birthDay);
        const month = parseInt(demographics.birthMonth);
        const year = parseInt(demographics.birthYear);
        
        if (day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 1900 && year <= new Date().getFullYear()) {
          const dayStr = day.toString().padStart(2, '0');
          const monthStr = month.toString().padStart(2, '0');
          dateOfBirth = `${year}-${monthStr}-${dayStr}`;
        } else {
          setError('Geçerli bir doğum tarihi gir');
          setLoading(false);
          return;
        }
      }

      const res = await apiPost<RegisterResponse>("/api/auth/register", {
        username: form.username,
        email: form.email,
        password: form.password,
        firstName: form.firstName.trim() || null,
        lastName: form.lastName.trim() || null,
        demographics: {
          dateOfBirth,
          gender: demographics.gender || null
        }
      });

      const t = res.access || res.token || res.accessToken;
      if (t) setToken(t);

      await refreshMe();
      nav("/feed");
    } catch (err: any) {
      if (err.message === 'username_taken' || err.code === 'username_taken') {
        setError('Sen benzersiz olmalısın.');
      } else if (err.message === 'email_taken' || err.code === 'email_taken') {
        setError('Bu e-posta adresi zaten kayıtlı. Giriş yapmayı dene.');
      } else if (err.code === 'bad_input' || err.message?.includes('bad_input')) {
        if (err.message?.includes('username')) {
          setError('Kullanıcı adı 3-24 karakter olmalı ve sadece harf, rakam, tire içerebilir.');
        } else if (err.message?.includes('email')) {
          setError('Geçerli bir e-posta adresi gir.');
        } else if (err.message?.includes('password')) {
          setError('Şifre en az 6 karakter olmalı.');
        } else {
          setError('Lütfen bilgilerini kontrol et.');
        }
      } else {
        const msg = err?.message && err.message.length < 200
          ? err.message
          : "Kayıt başarısız. Bilgilerini kontrol et.";
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="register-container">
      <div className="register-content">
        {/* Logo */}
        <div className="logo-container">
          <img src="/logo/logo.png" alt="SavNot" className="logo-image" />
          <h1 className="app-name">SavNot</h1>
        </div>

        <h2 className="register-title">Kayıt Ol</h2>

        <form onSubmit={onSubmit} className="register-form">
          {/* Username */}
          <input
            className="register-input"
            type="text"
            value={form.username}
            onChange={(e) => setForm({ ...form, username: e.target.value })}
            placeholder="Kullanıcı Adı"
            autoComplete="username"
            autoCapitalize="none"
            minLength={3}
            maxLength={24}
          />

          {/* First Name */}
          <input
            className="register-input"
            type="text"
            value={form.firstName}
            onChange={(e) => setForm({ ...form, firstName: e.target.value })}
            placeholder="Ad"
            autoComplete="given-name"
            maxLength={50}
          />

          {/* Last Name */}
          <input
            className="register-input"
            type="text"
            value={form.lastName}
            onChange={(e) => setForm({ ...form, lastName: e.target.value })}
            placeholder="Soyad"
            autoComplete="family-name"
            maxLength={50}
          />

          {/* Email */}
          <input
            className="register-input"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="E-posta"
            autoComplete="email"
          />

          {/* Password */}
          <input
            className="register-input"
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            placeholder="Şifre (min 6 karakter)"
            autoComplete="new-password"
            minLength={6}
          />

          {/* Password Confirm */}
          <input
            className="register-input"
            type="password"
            value={form.password2}
            onChange={(e) => setForm({ ...form, password2: e.target.value })}
            placeholder="Şifre Tekrarı"
            autoComplete="new-password"
            minLength={6}
          />

          {/* Demographics - Opsiyonel */}
          <div className="demographics-container">
            <h3 className="demographics-title">Opsiyonel Bilgiler</h3>
            
            {/* Birth Date */}
            <label className="demographics-label">Doğum Günü</label>
            <div className="date-container">
              <input
                className="date-input"
                type="number"
                value={demographics.birthDay}
                onChange={(e) => setDemographics({ ...demographics, birthDay: e.target.value })}
                placeholder="Gün"
                min="1"
                max="31"
                maxLength={2}
              />
              <input
                className="date-input"
                type="number"
                value={demographics.birthMonth}
                onChange={(e) => setDemographics({ ...demographics, birthMonth: e.target.value })}
                placeholder="Ay"
                min="1"
                max="12"
                maxLength={2}
              />
              <input
                className="date-input"
                type="number"
                value={demographics.birthYear}
                onChange={(e) => setDemographics({ ...demographics, birthYear: e.target.value })}
                placeholder="Yıl"
                min="1900"
                max={new Date().getFullYear()}
                maxLength={4}
              />
            </div>

            {/* Gender */}
            <label className="demographics-label">Cinsiyet</label>
            <div className="gender-container">
              {[
                { value: 'male', label: 'Erkek' },
                { value: 'female', label: 'Kadın' },
                { value: 'other', label: 'Diğer' },
                { value: 'prefer-not-to-say', label: 'Belirtmek İstemiyorum' }
              ].map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`gender-option ${demographics.gender === option.value ? 'gender-selected' : ''}`}
                  onClick={() => setDemographics({ 
                    ...demographics, 
                    gender: demographics.gender === option.value ? '' : option.value 
                  })}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Terms Acceptance */}
          <div className="terms-container">
            <h3 className="terms-preview-title">Kullanıcı Sözleşmesi Ön İzleme</h3>
            <div className="terms-preview-box">
              <p><strong>1. Hizmet Kullanımı ve Kabul</strong></p>
              <p>SavNot platformunu kullanarak bu kullanıcı sözleşmesini kabul etmiş sayılırsınız. Platform, kullanıcıların düşüncelerini özgürce paylaşabildiği, tartışabildiği ve etkileşime geçebildiği sosyal bir ortamdır. 18 yaşından küçük kullanıcıların veli izni ile kayıt olması gerekmektedir.</p>
              
              <p><strong>2. İçerik Sorumluluğu ve Yasaklar</strong></p>
              <p>Paylaştığınız tüm içeriklerden (gönderiler, yorumlar, görseller) münhasıran siz sorumlusunuz. Türkiye Cumhuriyeti yasalarına aykırı, hakaret içeren, ırkçılık yapan, nefret söylemi içeren, şiddet teşvik eden, pornografik veya başkalarının telif haklarını, özel hayatını veya şahsiyet haklarını ihlal eden içerikler paylaşamazsınız. Platform, yasadışı içerikleri silme ve hesabı askıya alma hakkını saklı tutar.</p>
              
              <p><strong>3. Hesap Güvenliği ve Sorumluluklar</strong></p>
              <p>Hesap bilgilerinizin (kullanıcı adı, şifre, e-posta) güvenliğinden tamamen siz sorumlusunuz. Şifrenizi kimseyle paylaşmayın, zayıf şifreler kullanmayın. Hesabınızdan yapılan tüm işlemlerden siz sorumlusunuz. Şüpheli aktivite fark ederseniz derhal şifrenizi değiştirin ve bizimle iletişime geçin.</p>
              
              <p><strong>4. Kişisel Verilerin Korunması (KVKK)</strong></p>
              <p>Kişisel verileriniz 6698 sayılı KVKK kapsamında korunur ve güvenle saklanır. Verileriniz rızanız olmadan üçüncü kişi veya kurumlarla paylaşılmaz, satılmaz veya kiralanmaz. Platform, sadece hizmet kalitesini artırmak ve yasal yükümlülüklerini yerine getirmek amacıyla verilerinizi işler. Verilerinizi istediğiniz zaman görüntüleyebilir, düzenleyebilir veya silebilirsiniz.</p>
              
              <p><strong>5. Platform Kuralları ve Yaptırımlar</strong></p>
              <p>Spam içerik paylaşımı, birden fazla sahte hesap oluşturma, otomatik bot kullanımı, sistemi manipüle etmeye çalışma, diğer kullanıcıları rahatsız etme veya taciz etme kesinlikle yasaktır. Bu kuralları ihlal eden hesaplar uyarı almadan geçici veya kalıcı olarak askıya alınabilir veya silinebilir.</p>
              
              <p><strong>6. Sorumluluk Reddi ve Yasal Uyarı</strong></p>
              <p>Platform, kullanıcıların paylaştığı içeriklerden sorumlu değildir. Her kullanıcı kendi içeriğinden münhasıran kendisi sorumludur. Platform, hizmetin kesintisiz olacağını garanti etmez. Teknik arızalar, bakım çalışmaları veya force majeure durumlarında hizmet kesintisi yaşanabilir. İhtilaf durumunda İstanbul Mahkemeleri ve İcra Daireleri yetkilidir.</p>
              
              <p className="terms-preview-more">✅ Yukarıdaki özet, sözleşmenin temel maddelerini içermektedir. Tam ve detaylı metni okumak için aşağıdaki linke tıklayınız.</p>
            </div>
            
            <label className="terms-checkbox-label">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="terms-checkbox"
              />
              <span className="terms-text">
                <Link to="/terms" target="_blank" className="terms-link">Kullanıcı Sözleşmesi</Link>'nin tamamını okudum ve kabul ediyorum
              </span>
            </label>
          </div>

          {/* Error */}
          {error && <p className="register-error">{error}</p>}

          {/* Register Button */}
          <button
            type="submit"
            className="register-button"
            disabled={loading}
          >
            {loading ? 'Yükleniyor...' : 'Kayıt Ol'}
          </button>

          {/* Login Link */}
          <p className="login-link">
            Hesabın var mı? <Link to="/login" className="login-link-bold">Giriş yap</Link>
          </p>
        </form>
      </div>

      <style>{`
        .register-container {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: #000;
          padding: 24px;
        }

        .register-content {
          width: 100%;
          max-width: 500px;
        }

        .logo-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          margin-bottom: 32px;
        }

        .logo-image {
          width: 80px;
          height: 80px;
          object-fit: contain;
        }

        .app-name {
          font-size: 24px;
          font-weight: 800;
          color: #114232;
          margin-top: 8px;
          margin-bottom: 0;
        }

        .register-title {
          font-size: 28px;
          font-weight: 800;
          color: #fff;
          margin-bottom: 24px;
          text-align: center;
        }

        .register-form {
          display: flex;
          flex-direction: column;
        }

        .register-input {
          background-color: #1a1a1a;
          border: 2px solid #114232;
          border-radius: 12px;
          padding: 16px;
          margin-bottom: 16px;
          font-size: 16px;
          color: #fff;
          width: 100%;
          box-sizing: border-box;
        }

        .register-input::placeholder {
          color: #666;
        }

        .register-input:focus {
          outline: none;
          border-color: #1a6b4f;
        }

        .demographics-container {
          background-color: #0a0a0a;
          border: 1px solid #1a1a1a;
          border-radius: 12px;
          padding: 20px;
          margin-bottom: 16px;
        }

        .demographics-title {
          font-size: 16px;
          font-weight: 700;
          color: #114232;
          margin: 0 0 16px 0;
        }

        .demographics-label {
          font-size: 14px;
          font-weight: 600;
          color: #888;
          margin-bottom: 8px;
          display: block;
        }

        .date-container {
          display: flex;
          gap: 8px;
          margin-bottom: 16px;
        }

        .date-input {
          background-color: #1a1a1a;
          border: 2px solid #114232;
          border-radius: 8px;
          padding: 12px;
          font-size: 16px;
          color: #fff;
          flex: 1;
          box-sizing: border-box;
        }

        .date-input::placeholder {
          color: #666;
        }

        .date-input:focus {
          outline: none;
          border-color: #1a6b4f;
        }

        .gender-container {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px;
        }

        .gender-option {
          background-color: #1a1a1a;
          border: 2px solid #333;
          border-radius: 8px;
          padding: 12px;
          font-size: 14px;
          color: #ccc;
          cursor: pointer;
          transition: all 0.2s;
        }

        .gender-option:hover {
          border-color: #114232;
        }

        .gender-option.gender-selected {
          background-color: #114232;
          border-color: #114232;
          color: #fff;
          font-weight: 600;
        }

        .terms-container {
          margin-top: 20px;
          margin-bottom: 16px;
        }

        .terms-preview-title {
          color: #114232;
          font-size: 16px;
          font-weight: 700;
          margin-bottom: 12px;
          text-align: center;
        }

        .terms-preview-box {
          background-color: #1a1a1a;
          border: 1px solid rgba(17, 66, 50, 0.3);
          border-radius: 12px;
          padding: 14px 16px;
          margin-bottom: 14px;
          font-size: 12px;
          line-height: 1.5;
          color: #ccc;
        }

        .terms-preview-box p {
          margin-bottom: 8px;
        }

        .terms-preview-box strong {
          color: #114232;
          font-weight: 600;
        }

        .terms-preview-more {
          color: #888;
          font-style: italic;
          text-align: center;
          margin-top: 8px;
          margin-bottom: 0;
        }

        .terms-checkbox-label {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          cursor: pointer;
          color: #ccc;
          font-size: 13px;
          padding: 0;
        }

        .terms-checkbox {
          width: 16px;
          height: 16px;
          cursor: pointer;
          accent-color: #114232;
          flex-shrink: 0;
          margin: 0;
        }

        .terms-text {
          line-height: 1.3;
        }

        .terms-link {
          color: #114232;
          text-decoration: none;
          font-weight: 600;
        }

        .terms-link:hover {
          text-decoration: underline;
        }

        .register-button {
          background-color: #114232;
          border: none;
          border-radius: 12px;
          padding: 16px;
          color: #fff;
          font-size: 16px;
          font-weight: 700;
          cursor: pointer;
          margin-top: 0;
          width: 100%;
        }

        .register-button:hover:not(:disabled) {
          background-color: #1a6b4f;
        }

        .register-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .register-error {
          color: #ff6b6b;
          text-align: center;
          margin-bottom: 8px;
          margin-top: 12px;
          font-size: 14px;
        }

        .login-link {
          color: #ccc;
          font-size: 14px;
          text-align: center;
          margin-top: 24px;
        }

        .login-link-bold {
          color: #114232;
          font-weight: 700;
          text-decoration: none;
        }

        .login-link-bold:hover {
          text-decoration: underline;
        }

        /* Remove spinner arrows from number inputs */
        input[type=number]::-webkit-inner-spin-button,
        input[type=number]::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }

        input[type=number] {
          -moz-appearance: textfield;
        }
      `}</style>
    </div>
  );
}
